import React from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    IconButton,
    Button,
    Divider,
    CircularProgress,
    Chip,
    useTheme,
    Skeleton,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    DoneAll as DoneAllIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNotifications } from '../../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '../../services/api/notificationApi';

const DashboardNotifications: React.FC = () => {
    const theme = useTheme();
    const PAGE_SIZE = 10;
    const {
        notifications,
        loading,
        loadingMore,
        hasMore,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refresh,
        loadMore,
    } = useNotifications({ pageSize: PAGE_SIZE });

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'success':
            case 'calculation_complete':
                return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
            case 'error':
            case 'calculation_error':
                return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
            case 'warning':
                return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
            case 'info':
            default:
                return <InfoIcon sx={{ color: theme.palette.info.main }} />;
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.isRead) {
            markAsRead(notification.id);
        }

        // Handle navigation based on notification metadata
        if (notification.metadata?.fileId) {
            globalThis.location.href = '/files/calculated';
        }
    };

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        deleteNotification(id);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                            fontSize: '1.5rem',
                        }}
                    >
                        Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                        Stay updated with your latest system activities
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton size="large" onClick={refresh} disabled={loading} title="Refresh">
                        <RefreshIcon fontSize="large" />
                    </IconButton>
                    {notifications.some(n => !n.isRead) && (
                        <IconButton size="large" onClick={markAllAsRead} title="Mark all as read" color="primary">
                            <DoneAllIcon fontSize="large" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    background: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(0,0,0,0.2)',
                    },
                }}
            >
                {(() => {
                  if (loading && notifications.length === 0) {
                    return (
                    <List sx={{ p: 0 }}>
                        {Array.from({ length: PAGE_SIZE }, (_, i) => `notif-skeleton-${i}`).map((rowKey, idx) => (
                            <React.Fragment key={rowKey}>
                                {idx > 0 && <Divider />}
                                <ListItem disablePadding>
                                    <Box sx={{ display: 'flex', gap: 2, width: '100%', py: 1.5, px: 2, alignItems: 'flex-start' }}>
                                        <Skeleton variant="circular" width={24} height={24} sx={{ mt: 0.25 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Skeleton variant="text" width="85%" sx={{ fontSize: '0.875rem' }} />
                                            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                                                <Skeleton variant="text" width={70} sx={{ fontSize: '0.75rem' }} />
                                                <Skeleton variant="rounded" width={90} height={18} sx={{ borderRadius: '4px' }} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                    );
                  }
                  if (notifications.length === 0) {
                    return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4, textAlign: 'center' }}>
                        <InfoIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            No notifications yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                            We'll notify you when something important happens
                        </Typography>
                    </Box>
                    );
                  }
                  return (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notification, index) => (
                            <React.Fragment key={notification.id}>
                                {index > 0 && <Divider />}
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={(e) => handleDelete(e, notification.id)}
                                            sx={{
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                '&:hover': { color: theme.palette.error.main },
                                            }}
                                            className="delete-button"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    }
                                    sx={{
                                        '&:hover .delete-button': {
                                            opacity: 0.6,
                                        },
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            backgroundColor: notification.isRead
                                                ? 'transparent'
                                                : `${theme.palette.primary.main}08`,
                                            '&:hover': {
                                                backgroundColor: notification.isRead
                                                    ? `${theme.palette.action.hover}`
                                                    : `${theme.palette.primary.main}12`,
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 2, width: '100%', pr: 4 }}>
                                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                                {getNotificationIcon(notification.type)}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: notification.isRead ? 400 : 600,
                                                            color: theme.palette.text.primary,
                                                            lineHeight: 1.4,
                                                        }}
                                                    >
                                                        {notification.message}
                                                    </Typography>
                                                    {!notification.isRead && (
                                                        <Box
                                                            sx={{
                                                                width: 6,
                                                                height: 6,
                                                                minWidth: 6,
                                                                borderRadius: '50%',
                                                                backgroundColor: theme.palette.primary.main,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        {formatDistanceToNow(new Date(notification.createdAt), {
                                                            addSuffix: true,
                                                        })}
                                                    </Typography>
                                                    {notification.metadata?.fileName && (
                                                        <Chip
                                                            label={notification.metadata.fileName}
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                backgroundColor: `${theme.palette.primary.main}15`,
                                                                color: theme.palette.primary.main,
                                                                fontWeight: 600,
                                                                border: `1px solid ${theme.palette.primary.main}20`,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            </React.Fragment>
                        ))}
                        {hasMore && (
                            <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    startIcon={loadingMore ? <CircularProgress size={14} /> : null}
                                >
                                    {loadingMore ? 'Loading...' : `Load more`}
                                </Button>
                            </Box>
                        )}
                    </List>
                    );
                })()}
            </Box>
        </Box>
    );
};

export default DashboardNotifications;
